import React, { useState, useEffect } from "react";
import { Box, Button, useMediaQuery } from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import DropAQueryv2 from "./Popup/DropAQueryv2";

export default function StickyFooter() {
  const isMobile = useMediaQuery("(max-width:600px)");
  const [openDropAQuery, setOpenDropAQuery] = useState(false);
  const [isFooterVisible, setIsFooterVisible] = useState(false);
  // Lazy-init (not useEffect) so pages with no #hero render already-visible
  // on the very first paint — otherwise the bar mounts hidden, then flips to
  // visible a tick later on every load, a deterministic full-width CLS hit.
  const [heroPassed, setHeroPassed] = useState(
    () => !document.getElementById("hero")
  );

  const handleCallNow = () => {
    window.open("tel:+918197868030", "_self");
  };

  const handleApplyFree = () => {
    setOpenDropAQuery(true);
  };

  // Hide the sticky footer once the real page footer scrolls into view.
  useEffect(() => {
    const footerElement = document.querySelector('[data-footer="v2"]');
    if (!footerElement) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsFooterVisible(entry.isIntersecting);
      },
      { threshold: 0.1 }
    );

    observer.observe(footerElement);
    return () => observer.disconnect();
  }, []);

  // Only show the sticky footer once the hero section has been scrolled past.
  // Pages that reuse this footer (blog / terms / refund / landing) have no
  // #hero — there, show it immediately so the footer never goes missing.
  useEffect(() => {
    const hero = document.getElementById("hero");
    if (!hero) {
      setHeroPassed(true);
      return;
    }

    const heroObserver = new IntersectionObserver(
      ([entry]) => setHeroPassed(!entry.isIntersecting),
      { threshold: 0 }
    );

    heroObserver.observe(hero);
    return () => heroObserver.disconnect();
  }, []);

  return (
    <>
      {/* WhatsApp Floating Icon */}
      <Box
        sx={{
          position: "fixed",
          bottom: { xs: 70, md: 76 },
          left: { xs: 16, md: 20 },
          width: { xs: 48, md: 52 },
          height: { xs: 48, md: 52 },
          borderRadius: "50%",
          backgroundColor: "#25D366",
          display: heroPassed ? "flex" : "none",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 4px 12px rgba(37, 211, 102, 0.3)",
          cursor: "pointer",
          transition: "all 0.3s ease",
          zIndex: 1299,
          "&:hover": {
            transform: "scale(1.1)",
            boxShadow: "0 6px 20px rgba(37, 211, 102, 0.5)",
          },
        }}
        onClick={() => window.open("https://wa.me/918114942378", "_blank")}
      >
        <WhatsAppIcon sx={{ color: "#fff", fontSize: { xs: 24, md: 28 } }} />
      </Box>

      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          width: "100%",
          zIndex: 1300,
          backgroundColor: "#fff",
          boxShadow: "0 -4px 16px rgba(0, 0, 0, 0.1)",
          paddingBottom: "env(safe-area-inset-bottom, 0)",
          display: heroPassed && !isFooterVisible ? "flex" : "none",
          gap: { xs: 1, md: 2 },
          px: { xs: 1.5, md: 2 },
          py: { xs: 1, md: 1.2 },
          justifyContent: "center",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        {/* Call Now Button */}
        <Button
          onClick={handleCallNow}
          sx={{
            background: "linear-gradient(135deg, #8C00FF 0%, #A020F0 100%)",
            color: "#fff",
            fontWeight: 700,
            fontSize: { xs: "12px", md: "14px" },
            px: { xs: 2.5, md: 10 },
            py: { xs: 1, md: 1.2 },
            borderRadius: "12px",
            textTransform: "none",
            whiteSpace: "nowrap",
            fontFamily: "Poppins, sans-serif",
            transition: "all 0.3s ease",
            boxShadow: "0 4px 12px rgba(140, 0, 255, 0.3)",
            flex: { xs: "1 1 45%", md: "0 0 auto" },
            "&:hover": {
              background: "linear-gradient(135deg, #A020F0 0%, #8C00FF 100%)",
              boxShadow: "0 6px 20px rgba(140, 0, 255, 0.4)",
              transform: "translateY(-2px)",
            },
          }}
        >
          📞 +91 8197868030
        </Button>

        {/* Apply Free Button */}
        <Button
          onClick={handleApplyFree}
          sx={{
            background: "#ff6b35",
            color: "#fff",
            fontWeight: 700,
            fontSize: { xs: "12px", md: "14px" },
            px: { xs: 2.5, md: 10 },
            py: { xs: 1, md: 1.2 },
            borderRadius: "12px",
            textTransform: "none",
            whiteSpace: "nowrap",
            fontFamily: "Poppins, sans-serif",
            transition: "all 0.3s ease",
            boxShadow: "0 4px 12px rgba(255, 107, 53, 0.3)",
            flex: { xs: "1 1 45%", md: "0 0 auto" },
            "&:hover": {
              background: "linear-gradient(135deg, #FFA500 0%, #FF6B35 100%)",
              boxShadow: "0 6px 20px rgba(255, 107, 53, 0.4)",
              transform: "translateY(-2px)",
            },
          }}
        >
          🚀 Apply Now
        </Button>

        <DropAQueryv2
          isSmallScreen={isMobile}
          open={openDropAQuery}
          onClose={() => setOpenDropAQuery(false)}
        />
      </Box>
    </>
  );
}
